import DownloadIcon from "@mui/icons-material/Download";
import PrintIcon from "@mui/icons-material/Print";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CloseIcon from "@mui/icons-material/Close";
import { Box, IconButton, Menu, MenuItem } from "@mui/material";
import { useState, useEffect } from "react";
import Flex from "../../../../../components/Flex";
import SideSheetDrawer from "../../../../../components/SidesheetDrawer";
import Text from "../../../../../components/Text";
import { DisclosureHost } from "../../../../../hooks/use-disclosure";

interface DocumentPreviewModalProps {
  file: File | string; // File object for pending attachments, string URL for sent attachments
  fileName?: string;
  fileType: "file" | "image";
  disclosureHost: DisclosureHost;
}

const DocumentPreviewModal = ({
  file,
  fileName,
  fileType,
  disclosureHost,
}: DocumentPreviewModalProps) => {
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(anchorEl);

  // Determine if file is a File object or URL string
  const isFileObject = file instanceof File;
  const displayFileName = isFileObject ? file.name : fileName || "Document";

  useEffect(() => {
    if (isFileObject) {
      // Create object URL for File object
      const url = URL.createObjectURL(file);
      setFileUrl(url);
      return () => {
        URL.revokeObjectURL(url);
      };
    } else {
      // Use URL string directly
      setFileUrl(file);
    }
  }, [file, isFileObject]);

  const handleDownload = () => {
    if (!fileUrl) return;

    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = displayFileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setAnchorEl(null);
  };

  const handlePrint = () => {
    if (!fileUrl) return;

    const printWindow = window.open(fileUrl, "_blank");
    if (printWindow) {
      printWindow.onload = () => {
        printWindow.print();
      };
    }
    setAnchorEl(null);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const getFileExtension = (name: string): string => {
    return name.split(".").pop()?.toLowerCase() || "";
  };

  const isPdf = (): boolean => {
    if (isFileObject) {
      return (
        file.type === "application/pdf" || getFileExtension(file.name) === "pdf"
      );
    }
    return getFileExtension(displayFileName) === "pdf";
  };

  const isImage = (): boolean => {
    if (isFileObject) {
      return file.type.startsWith("image/") || fileType === "image";
    }
    const ext = getFileExtension(displayFileName);
    return (
      ["jpg", "jpeg", "png", "gif", "webp"].includes(ext) ||
      fileType === "image"
    );
  };

  const renderPreview = () => {
    if (!fileUrl) {
      return (
        <Flex
          width="100%"
          height="60svh"
          justifyContent="center"
          alignItems="center"
        >
          <Text>No file available to preview</Text>
        </Flex>
      );
    }

    if (isPdf()) {
      return (
        <Box
          sx={{
            width: "100%",
            height: "calc(100svh - 120px)",
            border: "none",
          }}
        >
          <iframe
            src={fileUrl}
            style={{
              width: "100%",
              height: "100%",
              border: "none",
            }}
            title={displayFileName}
          />
        </Box>
      );
    }

    if (isImage()) {
      return (
        <Flex
          alignItems="center"
          justifyContent="center"
          height="calc(100svh - 120px)"
          width="100%"
          sx={{ overflow: "auto" }}
        >
          <Box
            component="img"
            src={fileUrl}
            alt={displayFileName}
            sx={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
            }}
          />
        </Flex>
      );
    }

    // For other file types, show download option
    return (
      <Flex
        width="100%"
        height="60svh"
        justifyContent="center"
        alignItems="center"
        flexDirection="column"
        gap={2}
      >
        <Text fontSize="16px">Preview not available for this file type</Text>
        <Text fontSize="14px" color="text.secondary">
          Click download to save the file
        </Text>
      </Flex>
    );
  };

  const headerActions = (
    <Flex gap={1} alignItems="center">
      <IconButton
        size="small"
        onClick={handleDownload}
        sx={{
          color: "text.primary",
          "&:hover": { bgcolor: "action.hover" },
        }}
        title="Download"
      >
        <DownloadIcon fontSize="small" />
      </IconButton>
      {isPdf() && (
        <IconButton
          size="small"
          onClick={handlePrint}
          sx={{
            color: "text.primary",
            "&:hover": { bgcolor: "action.hover" },
          }}
          title="Print"
        >
          <PrintIcon fontSize="small" />
        </IconButton>
      )}
      <IconButton
        size="small"
        onClick={handleMenuOpen}
        sx={{
          color: "text.primary",
          "&:hover": { bgcolor: "action.hover" },
        }}
        title="More options"
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        onClick={disclosureHost.close}
        sx={{
          color: "text.primary",
          "&:hover": { bgcolor: "action.hover" },
        }}
        title="Close"
      >
        <CloseIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={handleDownload}>
          <DownloadIcon fontSize="small" sx={{ mr: 1 }} />
          Download
        </MenuItem>
        {isPdf() && (
          <MenuItem onClick={handlePrint}>
            <PrintIcon fontSize="small" sx={{ mr: 1 }} />
            Print
          </MenuItem>
        )}
      </Menu>
    </Flex>
  );

  return (
    <SideSheetDrawer
      w="100vw"
      open={disclosureHost.opened}
      title={displayFileName}
      onClose={disclosureHost.close}
      withCloseIcon={false}
      endIcon={headerActions}
      pos="bottom"
    >
      <Box sx={{ height: "calc(100svh - 80px)", overflow: "hidden" }}>
        {renderPreview()}
      </Box>
    </SideSheetDrawer>
  );
};

export default DocumentPreviewModal;
